import apiClient from './apiClient';
import type { ImagingReportOcrPreview } from '@/types/imagingOrderOcr';

export interface ImagingOrder {
  id: string;
  patientId: string;
  /** Encounter (note session) this order was raised in, when applicable. */
  medicalRecordId?: string | null;
  patient?: {
    id: string;
    firstName: string;
    lastName: string;
  };
  orderedBy: string;
  orderer?: {
    id: string;
    firstName: string;
    lastName: string;
  };
  orderDate: string;
  status: 'ordered' | 'scheduled' | 'completed' | 'cancelled';
  priority: 'routine' | 'urgent' | 'stat';
  imagingType: 'X-Ray' | 'MRI' | 'CT' | 'Ultrasound' | 'PET' | 'Fluoroscopy' | 'Mammography' | 'Other';
  bodyPart: string;
  studyName?: string | null;
  laterality: 'left' | 'right' | 'bilateral' | 'N/A';
  clinicalIndication?: string;
  provisionalDiagnosis?: string;
  icdCode?: string;
  imagingCenter?: string;
  scheduledDate?: string;
  completedDate?: string;
  reportNotes?: string;
  reportAttachments?: unknown[];
  reviewed: boolean;
  reviewedBy?: string;
  reviewedAt?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateImagingOrderDto {
  patientId: string;
  /**
   * Optional link to the encounter that produced this order. Set when
   * the form is launched from /encounter/:id/review so the resulting
   * imaging order surfaces under that visit instead of floating
   * standalone.
   */
  medicalRecordId?: string;
  orderDate?: string;
  priority?: 'routine' | 'urgent' | 'stat';
  imagingType: string;
  bodyPart: string;
  /** Specific study name — required for 'Other' modality. */
  studyName?: string;
  laterality?: string;
  clinicalIndication?: string;
  provisionalDiagnosis?: string;
  icdCode?: string;
  imagingCenter?: string;
}

export interface UpdateImagingOrderDto extends Partial<Omit<CreateImagingOrderDto, 'patientId'>> {
  status?: string;
  scheduledDate?: string;
  completedDate?: string;
  reportNotes?: string;
  reportAttachments?: unknown[];
  reviewed?: boolean;
}

export interface FilterImagingOrderDto {
  patientId?: string;
  status?: string;
  priority?: string;
  imagingType?: string;
  reviewed?: boolean;
  search?: string;
  page?: string;
  limit?: string;
}

export const imagingOrderApi = {
  create: async (dto: CreateImagingOrderDto) => {
    const response = await apiClient.post('/imaging-orders', dto);
    return response.data;
  },

  getAll: async (filters?: FilterImagingOrderDto) => {
    const params = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== '') params.append(key, String(value));
      });
    }
    const response = await apiClient.get(`/imaging-orders${params.toString() ? `?${params.toString()}` : ''}`);
    // Backend wraps as { success, data: { data, total, page, limit } }. Unwrap
    // to the paginated object and normalize, so consumers always get an array
    // at `.data` (older shapes may return a bare array). Returning the raw
    // envelope made callers map over the wrapper → "map is not a function".
    const result = response.data?.data;
    if (Array.isArray(result)) {
      return { data: result, total: result.length, page: 1, limit: result.length };
    }
    return {
      data: result?.data ?? [],
      total: result?.total ?? 0,
      page: result?.page ?? 1,
      limit: result?.limit ?? 20,
    };
  },

  getById: async (id: string): Promise<ImagingOrder> => {
    const response = await apiClient.get(`/imaging-orders/${id}`);
    // Unwrap the { success, data } envelope to the order itself — consumers
    // read `order.id` etc. directly. Returning the envelope made them read
    // undefined fields → "Cannot read properties of undefined (reading 'slice')".
    return response.data?.data ?? response.data;
  },

  // Session-grouped list: one encounter (visit) per entry. Mirrors
  // prescriptionApi.listGroups — each group is the array of imaging orders
  // raised on that visit. Same envelope-normalization as getAll.
  listGroups: async (
    filters?: FilterImagingOrderDto,
  ): Promise<{ data: ImagingOrder[][]; total: number; page: number; limit: number }> => {
    const params = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== '') params.append(key, String(value));
      });
    }
    const response = await apiClient.get(
      `/imaging-orders/groups${params.toString() ? `?${params.toString()}` : ''}`,
    );
    const result = response.data?.data;
    return {
      data: result?.data ?? [],
      total: result?.total ?? 0,
      page: result?.page ?? 1,
      limit: result?.limit ?? 20,
    };
  },

  // All imaging orders in one visit's session group.
  getGroup: async (groupId: string): Promise<ImagingOrder[]> => {
    const response = await apiClient.get(`/imaging-orders/groups/${groupId}`);
    const result = response.data?.data ?? response.data;
    return Array.isArray(result) ? result : [];
  },

  getByPatient: async (patientId: string) => {
    const response = await apiClient.get(`/imaging-orders/patient/${patientId}`);
    return response.data;
  },

  getStats: async (patientId?: string) => {
    const params = patientId ? `?patientId=${patientId}` : '';
    const response = await apiClient.get(`/imaging-orders/stats${params}`);
    // Unwrap envelope so the list page's stat cards read real counts
    // (ordered/scheduled/completed/unreviewed) instead of undefined.
    return response.data?.data ?? response.data;
  },

  update: async (id: string, dto: UpdateImagingOrderDto) => {
    const response = await apiClient.patch(`/imaging-orders/${id}`, dto);
    return response.data;
  },

  delete: async (id: string) => {
    await apiClient.delete(`/imaging-orders/${id}`);
  },

  schedule: async (id: string, scheduledDate: string) => {
    const response = await apiClient.post(`/imaging-orders/${id}/schedule`, { scheduledDate });
    return response.data;
  },

  complete: async (id: string, data: { reportNotes?: string; reportAttachments?: unknown[] }) => {
    const response = await apiClient.post(`/imaging-orders/${id}/complete`, data);
    return response.data;
  },

  review: async (id: string) => {
    const response = await apiClient.post(`/imaging-orders/${id}/review`);
    return response.data;
  },

  cancel: async (id: string) => {
    const response = await apiClient.post(`/imaging-orders/${id}/cancel`);
    return response.data;
  },

  /**
   * AI-assisted report import. Multipart upload of an image or PDF
   * of the radiology report; backend OCRs + LLMs → returns a typed
   * preview (modality / body part / Findings / Impression / tags).
   * Does NOT write — caller commits via the existing `update` PATCH.
   */
  fromImage: async (
    id: string,
    file: File,
  ): Promise<ImagingReportOcrPreview> => {
    const form = new FormData();
    form.append('file', file, file.name);
    const response = await apiClient.post<{
      success: boolean;
      data: ImagingReportOcrPreview;
    }>(`/imaging-orders/${id}/ai/from-image`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      // Radiology PDFs run 2-3 pages — give the multi-page OCR room.
      timeout: 90_000,
    });
    return response.data.data;
  },
};
